/**
 * iggg estimate web — 유틸리티 함수
 * F-42/43-A + API Key 인증
 * + 프랜차이즈 브랜드 API 헬퍼 (Phase 1)
 */
import { AUTH_ENABLED, getIdToken, currentEmail } from "./auth/session";

// 배포 환경에서 VITE_API_URL 에 trailing slash 가 붙는 경우가 있어 정규화.
// (예: "https://api.example.com/" → "https://api.example.com")
const _rawApiBase =
  import.meta.env?.VITE_API_URL ||
  (typeof window !== "undefined" && window.__ENV__?.API_URL) ||
  "http://localhost:8000";
export const API_BASE_URL = String(_rawApiBase).replace(/\/+$/, "");

// 고객 노출 공개 페이지(서명/검증/열람/견적 공유)의 기준 도메인 — 항상 igggstudio.com 계열.
// 직원이 vercel.app/localhost 에서 복사한 링크가 고객에게 그대로 가는 사고 방지
// (사용자 확정 2026-07-20: 고객이 보는 웹주소는 전부 igggstudio.com). 백엔드
// 발송 링크의 IGGG_FRONT_ORIGIN 과 같은 의미의 프론트 짝.
export const PUBLIC_ORIGIN =
  import.meta.env?.VITE_PUBLIC_ORIGIN || "https://sign.igggstudio.com";

// API Key (모든 /api/* 요청에 X-API-Key 헤더로 첨부)
// 보안 (2026-06-11): 하드코딩 폴백 제거 — 공개 JS 번들에 키가 노출되던 구멍.
// VITE_API_KEY 미설정 빌드는 빈 키 → 백엔드 403 (fail-fast, silent 우회 금지).
export const API_KEY =
  import.meta.env?.VITE_API_KEY ||
  window.__ENV__?.API_KEY ||
  "";

// ── 날짜(KST 안전) ────────────────────────────────────────
// 규약: 사용자 노출 정본 날짜(계약일·발행일·견적일자·파일명 등)는 반드시 아래 헬퍼로 만든다.
// `new Date().toISOString().slice(0,10)` / `.split("T")[0]` 은 **UTC 기준**이라
// KST 00:00~08:59 에 하루 전 날짜가 찍힌다 — 사용 금지(하루 밀림 금지 규약).
// (UTC 자정으로 파싱된 ISO 날짜끼리의 연산처럼 in/out 이 모두 UTC 인 경우만 예외.)

/** Date → 로컬(브라우저 시간대=KST) 기준 'YYYY-MM-DD' */
export function localISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 오늘(로컬=KST) 'YYYY-MM-DD' — toISOString 대체 */
export function todayISO() {
  return localISODate(new Date());
}

/**
 * 공통 fetch 래퍼.
 * - X-API-Key 헤더 자동 첨부
 * - body가 FormData가 아니면 Content-Type: application/json 기본 설정
 *   (FormData일 때는 브라우저가 multipart boundary를 직접 세팅하도록 건드리지 않음)
 */
export async function apiFetch(url, options = {}) {
  const headers = { ...(options.headers || {}) };
  headers["X-API-Key"] = API_KEY;
  // G1 인증(VITE_AUTH=firebase): Bearer ID토큰 + 감사용 이메일 헤더 동봉.
  // 꺼져 있으면 no-op — 기존 X-API-Key 단독 동작 그대로.
  if (AUTH_ENABLED) {
    const t = await getIdToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
    const em = currentEmail();
    if (em && !headers["X-User-Email"]) headers["X-User-Email"] = em;
  }
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isFormData && options.body != null && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  return fetch(url, { ...options, headers });
}

/**
 * 저장 요청 전용 — **실패하면 반드시 던진다**(감사 2026-07-29 K묶음).
 *
 * `fetch` 는 서버가 4xx/5xx 를 돌려줘도 reject 하지 않는다(네트워크 오류만
 * reject). 그래서 `await apiFetch(...)` 뒤에 곧장 성공 상태를 그리면, 서버가
 * 거절한 저장이 **화면에서는 성공으로 보인다** — 미등록 단가 '등록 요청'이
 * 조용히 증발해 그 자재가 영영 미등록으로 남던 결함(#83)이 이 형태였다.
 *
 * 저장·생성·삭제처럼 **성공 여부가 화면 상태를 바꾸는** 호출은 이걸 쓴다.
 * 조회는 apiFetch 를 그대로 써도 된다(호출부가 res.ok 를 보고 폴백하므로).
 */
export async function apiSave(url, options = {}) {
  const res = await apiFetch(url, options);
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.clone().json();
      detail = body?.detail || body?.message || "";
    } catch { /* 본문이 JSON 이 아니면 상태코드만으로 충분하다 */ }
    const err = new Error(detail || `저장 실패 (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res;
}

// ── 프랜차이즈 브랜드 API 헬퍼 ──────────────────────────────

/** GET /api/brands → { brands: [...] } */
export async function fetchBrands() {
  const res = await apiFetch(`${API_BASE_URL}/api/brands`);
  if (!res.ok) throw new Error(`브랜드 목록 조회 실패 (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.brands || []);
}

/** GET /api/brands/{id} → 단일 브랜드 객체 */
export async function fetchBrand(id) {
  const res = await apiFetch(`${API_BASE_URL}/api/brands/${id}`);
  if (!res.ok) throw new Error(`브랜드 조회 실패 (${res.status})`);
  return res.json();
}

/** POST /api/brands → 생성된 브랜드 객체 반환 */
export async function createBrand(brandData) {
  const res = await apiFetch(`${API_BASE_URL}/api/brands`, {
    method: "POST",
    body: JSON.stringify(brandData),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `브랜드 생성 실패 (${res.status})`);
  }
  return res.json();
}

/** PUT /api/brands/{id} → partial update, 갱신된 브랜드 반환 */
export async function updateBrand(id, partial) {
  const res = await apiFetch(`${API_BASE_URL}/api/brands/${id}`, {
    method: "PUT",
    body: JSON.stringify(partial),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `브랜드 수정 실패 (${res.status})`);
  }
  return res.json();
}

/** DELETE /api/brands/{id} → soft delete, { ok: true, id } */
export async function deleteBrand(id) {
  const res = await apiFetch(`${API_BASE_URL}/api/brands/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `브랜드 삭제 실패 (${res.status})`);
  }
  return res.json();
}

// ── 프랜차이즈 견적 API 헬퍼 ──────────────────────────────

/** GET /api/franchise-estimates[?brand_id=x] → 목록 배열 */
export async function fetchFranchiseEstimates(brandId) {
  const qs = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : "";
  const res = await apiFetch(`${API_BASE_URL}/api/franchise-estimates${qs}`);
  if (!res.ok) throw new Error(`프랜차이즈 견적 목록 조회 실패 (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.estimates || data.items || data.data || []);
}

/** GET /api/franchise-estimates/{id} → 단일 견적 객체 */
export async function fetchFranchiseEstimate(id) {
  const res = await apiFetch(`${API_BASE_URL}/api/franchise-estimates/${id}`);
  if (!res.ok) throw new Error(`프랜차이즈 견적 조회 실패 (${res.status})`);
  return res.json();
}

/** POST /api/franchise-estimates → 생성된 견적 반환 */
export async function createFranchiseEstimate(data) {
  const res = await apiFetch(`${API_BASE_URL}/api/franchise-estimates`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // detail 이 배열(Pydantic validation errors)이면 개별 메시지 추출
    const detail = Array.isArray(body.detail)
      ? body.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
      : (body.detail || "");
    console.error("[createFranchiseEstimate] HTTP", res.status, detail || body);
    const e = new Error(`HTTP ${res.status}`);
    e.status = res.status;
    e.serverDetail = detail;
    e.body = body;
    throw e;
  }
  return res.json();
}

/** PUT /api/franchise-estimates/{id} → partial update, 갱신된 견적 반환 */
export async function updateFranchiseEstimate(id, patch) {
  const res = await apiFetch(`${API_BASE_URL}/api/franchise-estimates/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = Array.isArray(body.detail)
      ? body.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
      : (body.detail || "");
    console.error("[updateFranchiseEstimate] HTTP", res.status, detail || body);
    const e = new Error(`HTTP ${res.status}`);
    e.status = res.status;
    e.serverDetail = detail;
    e.body = body;
    throw e;
  }
  return res.json();
}

/** DELETE /api/franchise-estimates/{id} → soft delete */
export async function deleteFranchiseEstimate(id) {
  const res = await apiFetch(`${API_BASE_URL}/api/franchise-estimates/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `프랜차이즈 견적 삭제 실패 (${res.status})`);
  }
  return res.json();
}

/** POST /api/franchise-estimates/preview-calc → 실시간 계산 미리보기 */
export async function previewFranchiseCalc(payload) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/franchise-estimates/preview-calc`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  if (!res.ok) throw new Error(`미리보기 계산 실패 (${res.status})`);
  return res.json();
}

/** 프랜차이즈 견적 PDF URL (서버 렌더) */
export function getFranchisePdfUrl(id) {
  return `${API_BASE_URL}/api/franchise-estimates/${id}/pdf`;
}

/** 프랜차이즈 견적 PNG URL (서버 렌더) */
export function getFranchisePngUrl(id) {
  return `${API_BASE_URL}/api/franchise-estimates/${id}/png`;
}

/** GET /api/franchise-estimates/meta/separate-works → 별도공사 고정 메타 */
export async function fetchFranchiseSeparateMeta() {
  const r = await apiFetch(
    `${API_BASE_URL}/api/franchise-estimates/meta/separate-works`
  );
  if (!r.ok) throw new Error("Failed to fetch franchise meta");
  return r.json();
}
